import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHospital,
  faScissors,
  faUserDoctor,
  faHeart,
  faPaw,
  faMapMarkerAlt,
  faStar,
  faPhone,
  faArrowRight,
  faCalendarCheck
} from '@fortawesome/free-solid-svg-icons';
import Button from '../components/Button';

function Services() {
  const services = [
    {
      icon: faHospital,
      title: 'Veterinary Care',
      description: 'Expert medical care for your pets with state-of-the-art facilities and compassionate professionals.',
      features: ['Emergency Care', 'Routine Checkups', 'Surgery', 'Dental Care'],
      color: 'primary',
    },
    {
      icon: faScissors,
      title: 'Pet Grooming',
      description: 'Professional grooming services to keep your pet looking and feeling their best.',
      features: ['Full Service Bath', 'Nail Trimming', 'Hair Styling', 'Teeth Cleaning'],
      color: 'secondary',
    },
    {
      icon: faUserDoctor,
      title: 'Pet Training',
      description: 'Certified trainers help develop good behavior and strengthen the bond with your pet.',
      features: ['Obedience Training', 'Behavioral Therapy', 'Puppy Classes', 'Private Sessions'],
      color: 'accent',
    },
    {
      icon: faHeart,
      title: 'Pet Sitting',
      description: 'Trusted caregivers provide loving attention when you cannot be with your pet.',
      features: ['In-Home Care', 'Daily Walks', 'Feeding', 'Overnight Stays'],
      color: 'success',
    },
    {
      icon: faPaw,
      title: 'Pet Boarding',
      description: 'Safe and comfortable accommodations for your pet while you are away.',
      features: ['Climate Controlled', 'Playtime', 'Individual Attention', 'Medical Care'],
      color: 'warning',
    },
    {
      icon: faCalendarCheck,
      title: 'Pet Insurance',
      description: 'Comprehensive coverage plans to help manage your pet healthcare costs.',
      features: ['Accident Coverage', 'Illness Protection', 'Wellness Plans', 'Emergency Fund'],
      color: 'error',
    },
  ];

  const providers = [
    {
      name: 'VetCare Animal Hospital',
      type: 'Veterinary Clinic',
      rating: 4.9,
      reviews: 342,
      location: 'Downtown San Francisco',
      phone: '(555) 123-4567',
      features: ['24/7 Emergency', 'Surgery Center', 'Digital X-Ray'],
    },
    {
      name: 'Pawfect Grooming Spa',
      type: 'Pet Grooming',
      rating: 4.8,
      reviews: 156,
      location: 'Mission District',
      phone: '(555) 234-5678',
      features: ['Organic Products', 'Nail Art', 'Aromatherapy'],
    },
    {
      name: 'Good Dog Training Academy',
      type: 'Pet Training',
      rating: 4.9,
      reviews: 89,
      location: 'Sunset District',
      phone: '(555) 345-6789',
      features: ['Certified Trainers', 'Group Classes', 'Home Visits'],
    },
  ];

  return (
    <div className="bg-white dark:bg-neutral-900 min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-500 to-blue-700 dark:from-primary-600 dark:to-primary-800 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Professional Pet Services
            </h1>
            <p className="text-xl sm:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Find trusted, verified professionals for all your pet care needs. 
              From routine checkups to emergency care, we have you covered.
            </p>
            <Button 
              variant="secondary" 
              size="large"
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
              Find Services Near Me
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              Complete Pet Care Solutions
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-2xl mx-auto">
              Everything your pet needs for a happy, healthy life, provided by certified professionals in your area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-neutral-800 rounded-xl shadow-sm hover:shadow-lg transition-shadow p-6 border border-neutral-200 dark:border-neutral-700"
              >
                <div className={`w-16 h-16 bg-${service.color}-100 dark:bg-${service.color}-900 rounded-lg flex items-center justify-center mb-4`}>
                  <FontAwesomeIcon 
                    icon={service.icon} 
                    className={`h-8 w-8 text-${service.color}-600 dark:text-${service.color}-400`}
                  />
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-sm text-neutral-600 dark:text-neutral-300">
                      <div className={`w-2 h-2 bg-${service.color}-500 rounded-full mr-3`}></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button variant="outline" className="w-full">
                  Find Providers
                  <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Providers */}
      <section className="py-20 bg-neutral-50 dark:bg-neutral-800">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 dark:text-white mb-4">
              Featured Service Providers
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-300">
              Top-rated professionals trusted by thousands of pet families
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {providers.map((provider, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-neutral-700 rounded-xl shadow-sm p-6"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-1">
                      {provider.name}
                    </h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-2">
                      {provider.type}
                    </p>
                    <div className="flex items-center space-x-1 mb-2">
                      <FontAwesomeIcon icon={faStar} className="h-4 w-4 text-yellow-400" />
                      <span className="text-sm font-medium text-neutral-900 dark:text-white">
                        {provider.rating}
                      </span>
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">
                        ({provider.reviews} reviews)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-300">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="h-4 w-4 mr-2" />
                    {provider.location}
                  </div>
                  <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-300">
                    <FontAwesomeIcon icon={faPhone} className="h-4 w-4 mr-2" />
                    {provider.phone}
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {provider.features.map((feature, featureIndex) => (
                      <span 
                        key={featureIndex}
                        className="inline-block px-2 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200 text-xs rounded-md"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <Button variant="primary" size="small" className="flex-1">
                    <FontAwesomeIcon icon={faPhone} className="mr-1" />
                    Call
                  </Button>
                  <Button variant="outline" size="small" className="flex-1">
                    View Profile
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-blue-800 dark:from-primary-700 dark:to-primary-900">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <FontAwesomeIcon icon={faHeart} className="h-16 w-16 text-amber-300 mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Find the Best Care for Your Pet?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Browse verified professionals in your area and book appointments with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="secondary" 
              size="large"
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
              Search by Location
            </Button>
            <Button 
              variant="outline" 
              size="large"
              className="border-white text-white hover:bg-white hover:text-primary-600"
            >
              <FontAwesomeIcon icon={faCalendarCheck} className="mr-2" />
              Book Appointment
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;